/**
 * @file GoogleAuth.js
 * @description Handles the client-side Google OAuth 2.0 token flow for login and linking.
 */

import { googleLogin } from "./AuthService";
import { localData } from "../functions/api/localData";
import { setUserProfile } from "../account/profileData";
import { setSession } from "../utils/auth";

/**
 * Handles the successful login process after obtaining a Google access token.
 * Calls the backend login endpoint and manages session/profile state.
 * @async
 * @param {string} accessToken - Google OAuth 2.0 access token.
 * @param {Function} setProcessing - State setter for processing indicator.
 * @param {Function} setReport - State setter for user feedback messages.
 */
const handleGoogleLoginSuccess = async (accessToken, setProcessing, setReport) => {
  try {
    const response = await googleLogin(accessToken);
    if (response.status === 200) {
      const { user } = response.data;
      const privacyStatus = await localData("get", "privacy");
      if (privacyStatus === "accept") {
        user.last_refresh = new Date().toISOString();
        await setUserProfile(user);
        setSession(true);
        setReport({ show: true, message: "Logged in successfully using Google!", type: "success" });
      } else {
        await localData("remove", "privacy");
        localStorage.removeItem("session");
        setReport({
          show: true,
          message: "Login successful. Please review and accept our Privacy Policy to proceed.",
          type: "info",
        });
      }
    } else {
      setReport({ show: true, message: response.data.message || "Google login failed on backend.", type: "error" });
    }
  } catch (err) {
    console.error("Backend Google Login Error:", err);
    setReport({ show: true, message: err.message || "An error occurred during Google login.", type: "error" });
  } finally {
    setProcessing(false);
  }
};

/**
 * Handles errors during the Google login/linking process (e.g., token validation, backend errors).
 * @param {Error|object} error - The error object.
 * @param {Function} setProcessing - State setter for processing indicator.
 * @param {Function} setReport - State setter for user feedback messages.
 */
const handleGoogleLoginError = (error, setProcessing, setReport) => {
    console.error("Google Login Failed:", error);
    setReport({
      show: true,
      message: error?.details || error?.message || "Google login failed. Please try again.",
      type: "error",
    });
    setProcessing(false);
};

/**
 * Initiates the Google OAuth 2.0 token client flow.
 * Can be used for standard login or for obtaining a token for other actions (like linking)
 * by providing a custom onSuccessCallback.
 * @param {Function} setProcessing - State setter for processing indicator.
 * @param {Function} setReport - State setter for user feedback messages (primarily for flow initiation errors).
 * @param {Function} [onSuccessCallback] - Optional callback function to execute with the access token
 *                                         instead of the default login logic. This callback is responsible
 *                                         for its own API calls, reporting, and setting processing state to false.
 */
export const initiateGoogleLoginFlow = (
    setProcessing,
    setReport,
    onSuccessCallback
) => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    console.error("Google Client ID not found. Cannot initiate Google Login.");
    setReport({ show: true, message: "Google Login is not configured correctly.", type: "error" });
    return;
  }

  setProcessing(true);
  let callbackCompleted = false;

  try {
    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      throw new Error("Google Identity Services library not loaded.");
    }

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: googleClientId,
      scope: 'openid profile email',
      callback: async (tokenResponse) => {
        try {
            if (tokenResponse && tokenResponse.access_token) {
              if (typeof onSuccessCallback === 'function') {
                await onSuccessCallback(tokenResponse.access_token);
              } else {
                await handleGoogleLoginSuccess(tokenResponse.access_token, setProcessing, setReport);
              }
            } else {
               console.error("Google login callback received invalid tokenResponse:", tokenResponse);
               handleGoogleLoginError(new Error("Invalid token response from Google."), setProcessing, setReport);
            }
        } catch (callbackError) {
             console.error("Error within Google callback execution:", callbackError);
             if (typeof onSuccessCallback === 'function') {
                 setReport({ show: true, message: callbackError.message || "An error occurred after Google authentication.", type: "error" });
                 // Assume onSuccessCallback handles setProcessing(false) in its finally block
             } else {
                 // Handle potential errors from handleGoogleLoginSuccess if needed
             }
        } finally {
            callbackCompleted = true;
        }
      },
      error_callback: (error) => {
        if (!callbackCompleted) {
            if (typeof onSuccessCallback !== 'function') {
                 handleGoogleLoginError(error, setProcessing, setReport);
            } else {
                console.log("Google popup closed by user during custom callback flow.");
                setProcessing(false);
            }
        } else {
             console.log("Google popup closed after main callback completion.");
        }
      }
    });

    client.requestAccessToken();

  } catch (err) {
    console.error("Error initiating Google Login:", err);
    setReport({ show: true, message: err.message || "Failed to initiate Google Login.", type: "error" });
    setProcessing(false);
  }
};
